import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { FlaskConical, Trophy } from "lucide-react";

export default function Experiments() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/experiments")
      .then((r) => setData(r.data))
      .catch(() => setData({ variants: {}, labels: {} }));
  }, []);

  const variants = data?.variants || {};
  const labels = data?.labels || {};
  const keys = Object.keys(variants);
  const hasData = keys.some((k) => variants[k].impressions > 0);
  const leader = keys
    .filter((k) => variants[k].impressions > 0)
    .sort((a, b) => variants[b].conversion_rate - variants[a].conversion_rate)[0];

  return (
    <div data-testid="experiments-page">
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747] flex items-center gap-2">
          <FlaskConical className="h-3.5 w-3.5" /> Experiments
        </p>
        <h1 className="font-display text-4xl font-light mt-1">A/B Testing</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Landing hero headline test. Each visitor is randomly shown one variant; conversion is a
          signed up. Winner = highest signup rate.
        </p>
      </div>

      {!data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-6" data-testid="experiment-variants">
            {["A", "B"].map((k) => {
              const v = variants[k] || { impressions: 0, conversions: 0, conversion_rate: 0 };
              const isLeader = hasData && leader === k;
              return (
                <div
                  key={k}
                  className={`border p-6 bg-white ${isLeader ? "border-[#156F52] ring-1 ring-[#156F52]/30" : "border-zinc-200"}`}
                  data-testid={`experiment-variant-${k}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                      Variant {k}
                    </span>
                    {isLeader && (
                      <span className="inline-flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-widest text-[#156F52]" data-testid={`experiment-winner-${k}`}>
                        <Trophy className="h-3.5 w-3.5" /> Leading
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-display text-xl leading-snug text-[#0B1F33] min-h-[3.5rem]">
                    {labels[k] || "—"}
                  </p>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-display text-5xl text-[#0B1F33]" data-testid={`experiment-rate-${k}`}>
                      {v.conversion_rate}%
                    </span>
                    <span className="text-sm text-zinc-500">signup rate</span>
                  </div>
                  <div className="mt-5 grid grid-cols-2 divide-x divide-zinc-200 border-t border-zinc-200 pt-4">
                    <div>
                      <p className="font-display text-2xl" data-testid={`experiment-impressions-${k}`}>{v.impressions}</p>
                      <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Impressions</p>
                    </div>
                    <div className="pl-4">
                      <p className="font-display text-2xl" data-testid={`experiment-conversions-${k}`}>{v.conversions}</p>
                      <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Trial signups</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!hasData && (
            <p className="mt-6 text-sm text-zinc-500" data-testid="experiments-empty">
              No visitor data yet. Impressions appear as people land on the homepage; conversions when
              they sign up.
            </p>
          )}
        </>
      )}
    </div>
  );
}
